/**
 * Theme Preloader Service
 * Handles background theme preloading for known organizations
 */

import themeApiClient from './theme-api-client';
import performanceMonitor from '../utils/performance-monitor';

/**
 * ThemePreloader class
 */
class ThemePreloader {
  constructor() {
    this.preloadQueue = [];
    this.preloadedDomains = new Set();
    this.isPreloading = false;
  }

  /**
   * Preload a single theme in the background
   *
   * @param {string} domain - The organization domain/slug
   * @returns {Promise<void>}
   */
  async preloadTheme(domain) {
    if (this.preloadedDomains.has(domain)) {
      console.debug('Theme already preloaded for:', domain);
      return;
    }

    const endMeasure = performanceMonitor.startMeasure(`theme-preload-${domain}`);

    try {
      // Fetch theme (will be cached automatically)
      await themeApiClient.fetchBranding(domain);

      // Mark as preloaded
      this.preloadedDomains.add(domain);

      console.debug('Theme preloaded successfully for:', domain);
    } catch (error) {
      console.warn('Failed to preload theme for:', domain, error);
    } finally {
      endMeasure();
    }
  }

  /**
   * Preload multiple themes using requestIdleCallback
   *
   * @param {Array<string>} domains - Array of domain slugs
   * @returns {Promise<void>}
   */
  async preloadMultiple(domains) {
    if (!domains || domains.length === 0) {
      return;
    }

    // Filter out already preloaded domains
    const domainsToPreload = domains.filter(d => !this.preloadedDomains.has(d));

    if (domainsToPreload.length === 0) {
      console.debug('All themes already preloaded');
      return;
    }

    console.debug('Preloading themes for:', domainsToPreload);

    // Add to queue
    this.preloadQueue.push(...domainsToPreload);

    // Start preloading if not already running
    if (!this.isPreloading) {
      this.processPreloadQueue();
    }
  }

  /**
   * Process preload queue using requestIdleCallback
   */
  processPreloadQueue() {
    if (this.preloadQueue.length === 0) {
      this.isPreloading = false;
      return;
    }

    this.isPreloading = true;

    // Use requestIdleCallback for non-blocking preload
    if ('requestIdleCallback' in window) {
      requestIdleCallback(
        async (deadline) => {
          // Process items while we have idle time
          while (deadline.timeRemaining() > 0 && this.preloadQueue.length > 0) {
            const domain = this.preloadQueue.shift();
            await this.preloadTheme(domain);
          }

          // Continue processing if queue not empty
          if (this.preloadQueue.length > 0) {
            this.processPreloadQueue();
          } else {
            this.isPreloading = false;
          }
        },
        { timeout: 5000 }
      );
    } else {
      // Fallback for browsers without requestIdleCallback
      setTimeout(async () => {
        if (this.preloadQueue.length > 0) {
          const domain = this.preloadQueue.shift();
          await this.preloadTheme(domain);

          // Continue processing
          this.processPreloadQueue();
        }
      }, 100);
    }
  }

  /**
   * Preload common/popular organization themes
   *
   * @param {Array<string>} commonDomains - Array of common domain slugs
   */
  preloadCommon(commonDomains = []) {
    // Default common domains (can be customized)
    const defaultCommon = ['demo', 'test', 'example'];

    const domainsToPreload = commonDomains.length > 0 ? commonDomains : defaultCommon;

    this.preloadMultiple(domainsToPreload);
  }

  /**
   * Clear preloaded domains tracking
   */
  clearPreloadedTracking() {
    this.preloadedDomains.clear();
  }

  /**
   * Get preload statistics
   *
   * @returns {Object} Preload stats
   */
  getPreloadStats() {
    return {
      preloadedCount: this.preloadedDomains.size,
      queueLength: this.preloadQueue.length,
      isPreloading: this.isPreloading,
      preloadedDomains: Array.from(this.preloadedDomains)
    };
  }
}

// Export singleton instance
export default new ThemePreloader();

// Export class for custom instances
export { ThemePreloader };
